import { ILease } from "../services/leaseService";
import i18nIsoCountries from "i18n-nationality";
import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { DocumentType } from "../services/documentService";
import { IUnit } from "../services/unitService";
import i18next from "i18next";
import useFileViewerStore from "../stores/fileViewerStore";

export const getCurrentFormattedDate = (): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
    year: "numeric",
  };
  return new Date().toLocaleDateString("de-DE", options);
};

export const getFormattedDate = (datetime: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
    year: "numeric",
  };
  return new Date(datetime).toLocaleDateString("de-DE", options);
};

export const formatDateTimeToDate = (
  dateString: string,
  useGerman: boolean = true
): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const locale = useGerman ? "de-DE" : "en-US";
  const month = date.toLocaleString(locale, { month: "long" });
  const year = date.getFullYear();

  return `${day}. ${month} ${year}`;
};

export const formatGermanDate = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const isYouTubeUrl = (url: string): boolean => {
  return (
    url.match(/(youtu.be\/|youtube.com\/(watch\?(.*&)?v=|(embed|v)\/))/) != null
  );
};

export const getYouTubeId = (url: string): string | null => {
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return match && match[2].length === 11 ? match[2] : null;
};

export const formatMoney = (
  amount: number,
  showDecimals: boolean = true
): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  })
    .format(amount)
    .replace("$", "");
};

export const getActiveLease = (leases: ILease[]): ILease | null => {
  const currentDate = new Date();

  // "Active" means today sits between start_date and end_date AND a contract
  // exists. Without the start_date guard a signed renewal whose start_date is
  // still in the future would win the sort over the lease that's actually
  // currently running, hiding the current term's payments/cheques/deposit.
  const currentlyActiveLease = leases
    .filter(
      (lease) =>
        lease.documents.some(
          (document) => document.document_type === DocumentType.TENANCY_CONTRACT
        ) &&
        new Date(lease.start_date) <= currentDate &&
        new Date(lease.end_date) >= currentDate
    )
    .sort(
      (a, b) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime()
    );
  if (currentlyActiveLease.length > 0) return currentlyActiveLease[0];
  return getRunningUnsignedRenewal(leases);
};

const hasTenancyContract = (lease: ILease) =>
  lease.documents.some((document) => document.document_type === DocumentType.TENANCY_CONTRACT);

/**
 * A renewal whose term has begun while its contract is still being signed. The
 * tenant of the signed term before it stays in the unit, so the unit is still
 * let and this is the term running today, not a vacancy.
 */
export const getRunningUnsignedRenewal = (leases: ILease[]): ILease | null => {
  const currentDate = new Date();
  return (
    leases.find(
      (lease) =>
        !lease.terminated &&
        !lease.draft &&
        lease.renewed_from_lease_id != null &&
        !hasTenancyContract(lease) &&
        new Date(lease.start_date) <= currentDate &&
        new Date(lease.end_date) >= currentDate &&
        leases.some((previous) => previous.lease_id === lease.renewed_from_lease_id && hasTenancyContract(previous))
    ) ?? null
  );
};

// The lease that makes a unit count as "rented" for status display: the term
// running today if there is one, otherwise the soonest signed term whose
// start_date is still in the future. A signed contract awaiting move-in is
// already rented from the investor's view — getActiveLease can't say so
// because it requires today to sit inside the term (and must, so a future
// term never hides the running term's financials).
/**
 * A unit counts as reserved while a contract is being set up on it: either a
 * draft, or one that starts in the future and has no tenancy contract on file
 * yet.
 *
 * Terminated contracts are excluded, and that is the whole point of this
 * helper. A cancelled reservation is exactly `terminated = true` with a start
 * date still ahead, so without the check it matched the second condition and
 * kept the unit flagged as reserved after the reservation had been called off.
 *
 * The same four lines were copied into both dashboard cards, the units table
 * and the unit detail page, so the bug sat in all four.
 */
const startOfToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const isUnitReserved = (leases: ILease[] | undefined | null): boolean => {
  if (!leases) return false;

  const currentDate = new Date();

  // A draft holds the unit only until its term ends, as in the employee
  // portal: a reservation nobody closed must not keep the unit reserved.
  return leases.some(
    (lease) =>
      !lease.terminated &&
      (lease.draft
        ? new Date(lease.end_date) >= startOfToday()
        : new Date(lease.start_date) > currentDate &&
          !lease.documents.some(
            (document) => document.document_type === DocumentType.TENANCY_CONTRACT
          ))
  );
};

/**
 * A lease past its end with no renewal, notice or later term yet: the tenant
 * still lives in the unit while the renewal is agreed ("overdue renewal" in the
 * employee portal), so the unit is not vacant.
 */
export const getOverdueRenewalLease = (leases: ILease[]): ILease | null => {
  const today = startOfToday();
  return (
    leases
      .filter(
        (lease) =>
          !lease.draft &&
          !lease.terminated &&
          !lease.renewed &&
          new Date(lease.end_date) < today &&
          !leases.some(
            (later) => !later.draft && later.lease_id !== lease.lease_id && new Date(later.start_date) > new Date(lease.end_date)
          )
      )
      .sort((a, b) => new Date(b.end_date).getTime() - new Date(a.end_date).getTime())[0] ?? null
  );
};

export const getRentedLease = (leases: ILease[]): ILease | null => {
  const activeLease = getActiveLease(leases);
  if (activeLease) return activeLease;

  const currentDate = new Date();
  const upcomingSignedLeases = leases
    .filter(
      (lease) =>
        lease.documents.some(
          (document) => document.document_type === DocumentType.TENANCY_CONTRACT
        ) &&
        new Date(lease.start_date) > currentDate &&
        new Date(lease.end_date) >= currentDate
    )
    .sort(
      (a, b) =>
        new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
    );
  return upcomingSignedLeases.length > 0 ? upcomingSignedLeases[0] : null;
};

export const getCompletedLeases = (leases: ILease[]): ILease[] => {
  const currentDate = new Date();
  return leases.filter((lease) => new Date(lease.end_date) < currentDate);
};

export const censorIban = (iban: string) => {
  if (iban.length < 7) return iban;

  const start = iban.slice(0, 4);
  const end = iban.slice(-3);
  const middle = "****";

  return `${start}${middle}${end}`;
};

export const getNationalityInGerman = (
  code: string | null | undefined
): string => {
  if (!code) return "N/A";
  const lang = i18next.language === "en" ? "en" : "de";
  return i18nIsoCountries.getName(code.toUpperCase(), lang) || i18next.t("helpers.unknown");
};

export const getNationalityInEnglish = (
  code: string | null | undefined
): string => {
  if (!code) return "N/A";
  return i18nIsoCountries.getName(code.toUpperCase(), "en") || "Unknown";
};

/**
 * Downloads a Blob to the filesystem (native) or browser (web), and opens it if viewable.
 * @param blob Blob to download
 * @param fileName File name to save as (e.g. 'invoice.pdf')
 */
const VIEWABLE_FILE = /\.(pdf|png|jpe?g|webp|gif)$/i;

/**
 * PDFs and images open in the viewer, which has its own download; anything
 * else, like the Ejari ZIP or a CSV export, is saved right away.
 */
export const downloadAndOpenFile = async (blob: Blob, fileName: string) => {
  const type = blob.type || null;
  if (type === "application/pdf" || type?.startsWith("image/") || VIEWABLE_FILE.test(fileName)) {
    useFileViewerStore.getState().open({ name: fileName, blob, type });
    return;
  }
  await saveFile(blob, fileName);
};

/** Saves the file: a download on the web, the share sheet in the app. */
export const saveFile = async (blob: Blob, fileName: string) => {
  if (Capacitor.isNativePlatform()) {
    const buffer = await blob.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      )
    );

    await Filesystem.writeFile({
      path: fileName,
      data: base64,
      directory: Directory.Documents,
    });

    const filePath = await Filesystem.getUri({
      path: fileName,
      directory: Directory.Documents,
    });

    if (filePath.uri) {
      try {
        await Share.share({
          title: fileName,
          text: i18next.t("helpers.open_or_save_document"),
          url: filePath.uri,
        });
      } catch (err: any) {
        // Ignore "cancelled" errors (user dismissed the share sheet)
        if (err?.message?.toLowerCase().includes("cancel")) {
          // silently ignore
        } else {
          console.warn("Error during sharing file:", err);
        }
      }
    }
  } else {
    const blobUrl = window.URL.createObjectURL(blob);

    /*if (isViewable && !/Mobi/i.test(navigator.userAgent)) {
      await openExternalLink(blobUrl);
    } else {*/
    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();

    window.URL.revokeObjectURL(blobUrl);
    document.body.removeChild(anchor);
    //}
  }
};

/**
 * Buildings the appraisal is live for. The Vybe launched first, the rest
 * followed on 6 August 2026.
 */
export const APPRAISAL_LIVE_PROPERTY_IDS = [
  1, // The Haven Residences
  2, // The Ivy
  3, // The Vybe
  4, // The Haven Gardens
];

export const isPropertyLiveForAppraisal = (propertyId: number): boolean =>
  APPRAISAL_LIVE_PROPERTY_IDS.includes(propertyId);

/**
 * Some units reached us without their SPA, and the tax firm needs it. The
 * booking is held until the investor uploads it.
 */
export const needsPurchaseContractUpload = (unit: IUnit): boolean =>
  unit.requires_spa_upload === true &&
  !(unit.documents ?? []).some(
    (document) => document.document_type === DocumentType.PURCHASE_CONTRACT
  );

/**
 * A unit can only be ordered once — an existing order or an already uploaded
 * appraisal takes it out of the running.
 */
export const isUnitOrderableForAppraisal = (unit: IUnit): boolean =>
  // A service for management customers only.
  !!unit.pm_contract &&
  isPropertyLiveForAppraisal(unit.property.property_id) &&
  unit.key_handover &&
  !unit.property.under_construction &&
  !unit.appraisal_order &&
  !(unit.documents ?? []).some(
    (document) => document.document_type === DocumentType.RESIDUAL_VALUE_APPRAISAL
  );


/**
 * The date a transaction is shown and sorted by.
 *
 * A payout is stamped by the run that executes it, so `payment_date` is the
 * day the money actually left. Everything else is dated by when it arose.
 *
 * For that second group the earliest of the two known timestamps wins, and
 * that is the whole trick: both ways this data goes wrong push a date
 * *later*, never earlier.
 *
 * - The 712 records taken over on 2 June 2025 carry the import moment in
 *   `created_at`, months after the payments themselves. Their `payment_date`
 *   survived, is earlier, and wins.
 * - The payout run overwrites `payment_date` on every item it settles with
 *   its own timestamp. That is always after the item arose, so `created_at`
 *   wins and a rent no longer shares the payout's day.
 *
 * `due_date` is deliberately not used: on a settled item it is a planned
 * date, not a fact.
 */
export const getTransactionDate = (payment: {
  purpose?: string | null;
  payment_date?: string | null;
  created_at: string;
}): string => {
  if (payment.purpose === "payout") {
    return payment.payment_date || payment.created_at;
  }

  if (!payment.payment_date) return payment.created_at;

  return new Date(payment.payment_date) < new Date(payment.created_at)
    ? payment.payment_date
    : payment.created_at;
};
